import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalForm } from 'src/components/form/form/ModalForm';

import { useInsertThirdPartyContact } from '@/hooks/mutations/third-party-contact';

import { AddContactForm } from './AddContactForm';
import type { AddContactSchemaFields } from './schema';
import { addContactDefaultValues, createAddContactSchema } from './schema';

interface Props {
  thirdPartyId: string;
  existingEmails: string[];
  isVisible: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
}

export const AddContactModal: FC<Props> = ({
  thirdPartyId,
  existingEmails,
  isVisible,
  onDismiss,
  onSuccess,
}) => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'third_party.contacts',
  });

  const schema = useMemo(
    () => createAddContactSchema(existingEmails),
    [existingEmails]
  );

  const { insertThirdPartyContact } = useInsertThirdPartyContact();

  const onSave = async (values: AddContactSchemaFields) => {
    await insertThirdPartyContact({
      ...values,
      ThirdPartyId: thirdPartyId,
    });
    onSuccess();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <ModalForm<AddContactSchemaFields>
      defaultValues={addContactDefaultValues}
      i18n={{ entity_name: t('entity_name') }}
      schema={schema}
      onSave={onSave}
      onDismiss={() => onDismiss()}
      formId={'add-contact-form'}
      testId={'addContactModal'}
      visible={isVisible}
      readOnly={false}
    >
      <AddContactForm />
    </ModalForm>
  );
};
